// DeckRail — left-side tool strip on the deck view.
//
// (Filename kept as AdminRail.tsx for now to avoid churning every
// caller; default export name stays the same. The rail is no longer
// admin-only — workspace is visible to anyone who can see the deck;
// the inspector stays admin-OR-owner-gated.)
//
// Mounts at the deck-view parent level (next to ChatPanel, not inside
// PreviewPanel) so the rail is always visible regardless of:
//   - what phase the deck is in (gather / plan / preview),
//   - which tab the user has open (Brief / Storyboard / Notes / Presentation),
//   - whether signals.artifacts has populated yet,
//   - whether the deck is in an error state.
//
// Buttons (top → bottom):
//   1. Workspace — task R folder (artifacts/, memory/, context/).
//      Click opens WorkspaceDrawer; reuses InlineFileViewer for previews.
//   2. Inspector — diagnostic drawer (CLAUDE.md, tulpa+engine_config,
//      R2 listing, recent activity + messages). Hidden by default;
//      surface only via the showInspectorButton prop.
//
// Prefetching strategy: when the rail mounts we kick off the workspace
// listing and the agent-profile fetch in parallel and cache the
// responses here. Click on either button hands the drawer a ready
// snapshot so the first paint is instant; the drawer can still refresh
// to pick up live changes. This avoids the previous click → 400-600ms
// wait pattern.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bug, FolderOpen } from '@phosphor-icons/react';
import { apiFetch } from '../api/auth-fetch';
import InspectorDrawer from './InspectorDrawer';
import WorkspaceDrawer, { type WorkspaceData } from './WorkspaceDrawer';

interface AdminRailProps {
  deckId: string;
  /** When true, render the Inspector button. Off by default — the
   *  diagnostic surface (CLAUDE.md, engine_config) is a power-user view;
   *  workspace + profile cover the everyday cases. */
  showInspectorButton: boolean;
}

export default function AdminRail({ deckId, showInspectorButton }: AdminRailProps) {
  const [showWorkspace, setShowWorkspace] = useState(false);
  const [showInspector, setShowInspector] = useState(false);

  // Workspace prefetch — kicked off on mount so click → instant render.
  const [workspaceData, setWorkspaceData] = useState<WorkspaceData | null>(null);
  const [workspaceError, setWorkspaceError] = useState('');
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const workspaceLoadId = useRef(0);

  const loadWorkspace = useCallback(async () => {
    if (!deckId) return;
    const id = ++workspaceLoadId.current;
    // Clear the previous deck's snapshot before fetching the new one,
    // so a deckId change paints the skeleton rather than the prior
    // deck's files. Without this, switching decks briefly surfaces the
    // last deck's memory/context/artifacts in the drawer.
    setWorkspaceData(null);
    setWorkspaceLoading(true);
    setWorkspaceError('');
    try {
      const res = await apiFetch(`/api/decks/${deckId}/workspace`);
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status} ${text.slice(0, 200)}`);
      }
      const j = (await res.json()) as WorkspaceData;
      if (id === workspaceLoadId.current) setWorkspaceData(j);
    } catch (e: any) {
      if (id === workspaceLoadId.current) {
        setWorkspaceError(e?.message || 'Failed to load.');
      }
    } finally {
      if (id === workspaceLoadId.current) setWorkspaceLoading(false);
    }
  }, [deckId]);

  // Prefetch on mount / deckId change. Also pre-warm /agent-profile so
  // the chat avatar's popover opens instantly — that endpoint is
  // cached by the SW / browser HTTP cache after the first response.
  useEffect(() => {
    if (!deckId) return;
    void loadWorkspace();
    // Fire-and-forget agent-profile prefetch. We don't store the body
    // here — AgentProfilePopover refetches on open, but the response
    // is sitting in HTTP cache so it returns immediately.
    void apiFetch(`/api/decks/${deckId}/agent-profile`).catch(() => {});
  }, [deckId, loadWorkspace]);

  const railStyle: React.CSSProperties = {
    width: 36,
    flexShrink: 0,
    background: 'var(--bg-sidebar)',
    borderRight: '1px solid var(--border)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '8px 0',
    gap: 4,
  };

  const btnStyle: React.CSSProperties = {
    width: 28,
    height: 28,
    border: 'none',
    borderRadius: 6,
    background: 'transparent',
    color: 'var(--text-muted)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s',
  };

  const hoverOn = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.background = 'var(--code-bg)';
    e.currentTarget.style.color = 'var(--text-primary)';
  };
  const hoverOff = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.background = 'transparent';
    e.currentTarget.style.color = 'var(--text-muted)';
  };

  return (
    <>
      <aside style={railStyle} aria-label="Deck tools">
        <button
          onClick={() => setShowWorkspace(true)}
          style={btnStyle}
          title="Workspace — files produced by the agent"
          onMouseOver={hoverOn}
          onMouseOut={hoverOff}
        >
          <FolderOpen size={16} weight="light" />
        </button>
        {showInspectorButton && (
          <button
            onClick={() => setShowInspector(true)}
            style={btnStyle}
            title="Inspect agent state (admin / owner)"
            onMouseOver={hoverOn}
            onMouseOut={hoverOff}
          >
            <Bug size={16} weight="light" />
          </button>
        )}
      </aside>
      {showWorkspace && (
        <WorkspaceDrawer
          deckId={deckId}
          open={showWorkspace}
          onClose={() => setShowWorkspace(false)}
          initialData={workspaceData}
          initialLoading={workspaceLoading}
          initialError={workspaceError}
          onRefresh={loadWorkspace}
        />
      )}
      {showInspector && (
        <InspectorDrawer
          deckId={deckId}
          open={showInspector}
          onClose={() => setShowInspector(false)}
        />
      )}
    </>
  );
}
